// src/pages/StarRec/Dashboard.jsx
import React, { useContext, useEffect, useMemo, useState } from "react"; import { Link } from "react-router-dom"; import { ThemeContext } from "../../contexts/ThemeContext";
import { blacklistApi } from "../../api";
import { jobHolderApi } from "../../api";

const readJSON = (key, fallback) => { try { const raw = localStorage.getItem(key); if (!raw) return fallback; const v = JSON.parse(raw); return v ?? fallback; } catch { return fallback; } };
const countOf = (arr) => (Array.isArray(arr) ? arr.length : 0);
const nowText = () => new Date().toLocaleString();

export default function Dashboard() {
  const { setTitle } = useContext(ThemeContext);
  const [tick, setTick] = useState(0);
  const [timeNow, setTimeNow] = useState(nowText());

  useEffect(() => { setTitle("StarRec Dashboard"); }, [setTitle]);
  useEffect(() => { const t = setInterval(() => setTimeNow(nowText()), 15000); return () => clearInterval(t); }, []);

  // sync blacklist counts from API into localStorage (fallback for existing dashboard logic)
  useEffect(() => {
    let mounted = true;

    async function fetchAndSync() {
      try {
        const res = await blacklistApi.getBlacklisted();
        if (!mounted) return;
        const biz = Array.isArray(res?.businesses) ? res.businesses : [];
        const emp = Array.isArray(res?.employees) ? res.employees : [];
        try {
          localStorage.setItem("starrec_businesses_blacklisted", JSON.stringify(biz));
          localStorage.setItem("starrec_jobholders_blacklisted", JSON.stringify(emp));
        } catch (_) {}
      } catch (e) {
        console.debug("blacklistApi.getBlacklisted() failed:", e?.message || e);
      }

      try {
        const active = await jobHolderApi.getEmployees({ blacklisted: false });
        if (!mounted) return;
        try {
          localStorage.setItem("starrec_jobholders_active", JSON.stringify(Array.isArray(active) ? active : []));
        } catch (_) {}
      } catch (e) {
        console.debug("jobHolderApi.getEmployees() failed:", e?.message || e);
      }
    }

    fetchAndSync();

    return () => { mounted = false; };
  }, [tick]);

  const data = useMemo(() => {
    const pending = readJSON("starrec_businesses_pending", []);
    const approved = readJSON("starrec_businesses_approved", []);
    const rejected = readJSON("starrec_businesses_rejected", []);
    const blacklistedBiz = readJSON("starrec_businesses_blacklisted", []);

    const jobHoldersActive = readJSON("starrec_jobholders_active", []);
    const jobHoldersBlacklisted = readJSON("starrec_jobholders_blacklisted", []);

    const blacklistRequests = readJSON("starrec_blacklist_requests", []);
    const activity = readJSON("starrec_activity", []);

    const totalBizReviewed = countOf(approved) + countOf(rejected);
    const totalBizAll = countOf(pending) + totalBizReviewed;

    const approvalRate = totalBizReviewed === 0 ? 0 : Math.round((countOf(approved) / totalBizReviewed) * 100);
    const rejectionRate = totalBizReviewed === 0 ? 0 : Math.round((countOf(rejected) / totalBizReviewed) * 100);
    const blacklistRate = totalBizAll === 0 ? 0 : Math.round((countOf(blacklistedBiz) / totalBizAll) * 100);

    const act = Array.isArray(activity) ? activity.slice().reverse().slice(0, 8) : [];

    return {
      pending, approved, rejected, blacklistedBiz, jobHoldersActive, jobHoldersBlacklisted, blacklistRequests, act,
      counts: {
        pending: countOf(pending),
        approved: countOf(approved),
        rejected: countOf(rejected),
        blacklistedBiz: countOf(blacklistedBiz),
        jobHoldersActive: countOf(jobHoldersActive),
        jobHoldersBlacklisted: countOf(jobHoldersBlacklisted),
        blacklistRequests: countOf(blacklistRequests),
      },
      rates: { approvalRate, rejectionRate, blacklistRate }
    };
  }, [tick]);

  const addActivity = (type, message) => {
    const prev = readJSON("starrec_activity", []);
    const next = Array.isArray(prev) ? prev : [];
    next.push({ at: new Date().toLocaleString(), type, message });
    localStorage.setItem("starrec_activity", JSON.stringify(next));
    setTick((p) => p + 1);
  };

  const StatCard = ({ title, value, subtitle, to, btnText, gradient, icon }) => (
    <div className="col-xl-3 col-md-6">
      <div className="card border-0 shadow-sm h-100" style={{ overflow: "hidden" }}>
        <div className="card-body text-white" style={{ background: gradient }}>
          <div className="d-flex align-items-start justify-content-between">
            <div>
              <div className="small opacity-75">{title}</div>
              <div className="display-6 fw-bold mb-1">{value}</div>
              <div className="small opacity-75">{subtitle}</div>
            </div>
            <div className="fs-1" style={{ lineHeight: 1 }}>{icon}</div>
          </div>
          <div className="mt-3">
            <Link className="btn btn-sm btn-light" to={to}>{btnText}</Link>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="container py-4">
      <div className="d-flex flex-wrap align-items-center justify-content-between mb-3 gap-2">
        <div>
          <h1 className="fs-3 fw-bold mb-1">StarRec Dashboard</h1>
          <div className="text-muted small">Last updated: {timeNow}</div>
        </div>
        <div className="d-flex gap-2">
          <button className="btn btn-outline-secondary" onClick={() => setTick((p) => p + 1)}>Refresh</button>
          <button className="btn btn-outline-secondary" onClick={() => addActivity("system", "Manual refresh triggered")}>Log Activity</button>
        </div>
      </div>

      {/* KPI CARDS */}
      <div className="row g-3 mb-4">
        <StatCard title="Businesses Pending" value={data.counts.pending} subtitle="Waiting for verification" to="/StarRec/businesses/approve" btnText="Open Approvals" gradient="linear-gradient(135deg, #ff8a00, #e52e71)" icon="🕒" />
        <StatCard title="Approved Businesses" value={data.counts.approved} subtitle={`Approval rate: ${data.rates.approvalRate}%`} to="/StarRec/businesses/manage" btnText="Manage Businesses" gradient="linear-gradient(135deg, #00b09b, #96c93d)" icon="✅" />
        <StatCard title="Blacklisted Businesses" value={data.counts.blacklistedBiz} subtitle={`Blacklist share: ${data.rates.blacklistRate}%`} to="/StarRec/blacklists/businesses" btnText="View Blacklist" gradient="linear-gradient(135deg, #232526, #414345)" icon="⛔" />
        <StatCard title="Blacklist Requests" value={data.counts.blacklistRequests} subtitle="New + pending requests" to="/StarRec/blacklists/requests" btnText="Review Requests" gradient="linear-gradient(135deg, #2193b0, #6dd5ed)" icon="📨" />
      </div>

      {/* MID SECTION */}
      <div className="row g-3 mb-4">
        {/* Status breakdown */}
        <div className="col-lg-6">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-header bg-white fw-semibold">Verification Summary</div>
            <div className="card-body">
              <div className="d-flex justify-content-between small mb-1"><span>Approved</span><span>{data.counts.approved}</span></div>
              <div className="progress mb-3" style={{ height: 10 }}>
                <div className="progress-bar bg-success" role="progressbar" style={{ width: `${data.rates.approvalRate}%` }} aria-valuenow={data.rates.approvalRate} aria-valuemin="0" aria-valuemax="100" />
              </div>

              <div className="d-flex justify-content-between small mb-1"><span>Rejected</span><span>{data.counts.rejected}</span></div>
              <div className="progress mb-3" style={{ height: 10 }}>
                <div className="progress-bar bg-danger" role="progressbar" style={{ width: `${data.rates.rejectionRate}%` }} aria-valuenow={data.rates.rejectionRate} aria-valuemin="0" aria-valuemax="100" />
              </div>

              <div className="d-flex justify-content-between small mb-1"><span>Pending</span><span>{data.counts.pending}</span></div>
              <div className="progress" style={{ height: 10 }}>
                <div className="progress-bar bg-warning" role="progressbar" style={{ width: `${Math.min(100, data.counts.pending * 5)}%` }} aria-valuenow={Math.min(100, data.counts.pending * 5)} aria-valuemin="0" aria-valuemax="100" />
              </div>

              <div className="text-muted small mt-3">Tip: Pending progress is visual-only (not a percentage) unless you add totals from backend later.</div>
            </div>
          </div>
        </div>

        {/* Job holders snapshot */}
        <div className="col-lg-6">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-header bg-white fw-semibold">Job Holders Snapshot</div>
            <div className="card-body">
              <div className="row g-3">
                <div className="col-sm-6">
                  <div className="p-3 rounded-3 border">
                    <div className="text-muted small">Active Job Holders</div>
                    <div className="fs-2 fw-bold">{data.counts.jobHoldersActive}</div>
                    <Link className="btn btn-sm btn-outline-primary mt-2" to="/StarRec/job-holders/manage">Manage</Link>
                  </div>
                </div>
                <div className="col-sm-6">
                  <div className="p-3 rounded-3 border">
                    <div className="text-muted small">Blacklisted Job Holders</div>
                    <div className="fs-2 fw-bold">{data.counts.jobHoldersBlacklisted}</div>
                    <Link className="btn btn-sm btn-outline-primary mt-2" to="/StarRec/blacklists/job-holders">View</Link>
                  </div>
                </div>
                <div className="col-12">
                  <div className="d-flex flex-wrap gap-2">
                    <Link className="btn btn-outline-secondary" to="/StarRec/accounting">Accounting</Link>
                    <Link className="btn btn-outline-secondary" to="/StarRec/settings/business-entities">Business Entities</Link>
                    <Link className="btn btn-outline-secondary" to="/StarRec/settings/business-categories">Business Categories</Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* RECENT ACTIVITY */}
      <div className="card border-0 shadow-sm">
        <div className="card-header bg-white d-flex align-items-center justify-content-between">
          <div className="fw-semibold">Recent Activity</div>
          <button className="btn btn-sm btn-outline-secondary" onClick={() => { localStorage.removeItem("starrec_activity"); setTick((p) => p + 1); }}>Clear</button>
        </div>

        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table mb-0 align-middle">
              <thead className="table-light">
                <tr>
                  <th style={{ width: "22%" }}>Time</th>
                  <th style={{ width: "16%" }}>Type</th>
                  <th>Message</th>
                </tr>
              </thead>
              <tbody>
                {data.act.length === 0 ? (
                  <tr><td colSpan={3} className="text-center text-muted py-4">No activity yet</td></tr>
                ) : (
                  data.act.map((a, i) => (
                    <tr key={i}>
                      <td className="text-muted">{a?.at || "-"}</td>
                      <td>
                        <span className={`badge ${a?.type === "approve" ? "bg-success" : a?.type === "reject" ? "bg-danger" : a?.type === "blacklist" ? "bg-dark" : "bg-secondary"}`}>
                          {a?.type || "info"}
                        </span>
                      </td>
                      <td>{a?.message || "-"}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="text-muted small mt-3">
        This dashboard reads counts from localStorage keys (example): <code>starrec_businesses_pending</code>, <code>starrec_businesses_approved</code>, <code>starrec_businesses_blacklisted</code>, <code>starrec_activity</code>.
      </div>
    </div>
  );
}
